import { HttpClient } from '@angular/common/http';
import { Injectable, TransferState, makeStateKey } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import type { CraftXGritProductEntry } from '@core-mkt/interfaces/craft-xgrit-product-entry';
import { XGritApiService } from '@core-mkt/services/xgrit-api/xgrit-api.service';
import type { XGritApiData, XGritCompleteProduct } from '@core-mkt/services/xgrit-api/xgrit-product';
import type {
  ProductCodeLists,
  XGritPurchaseReceiptPriceResponse,
  XgritPurchaseReceiptMultipriceResponse,
} from '@core-mkt/services/xgrit-api/xgrit-purchase-receipt-price-response';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import type { LogError } from '../../../shared/lib/logger';
import { SEVERITY, log, logError } from '../../../shared/lib/logger';
import type { CouponCollectionRequest } from '../coupon-aggregation/coupon-aggregation.service';
import { CouponAggregationService } from '../coupon-aggregation/coupon-aggregation.service';
import { QueryParamService } from '../query-param/query-param.service';

/**
 * The slice of a `purchase-receipt/price` line item this service reads. `lineItemList` is
 * untyped (`any[]`) on the shared response interface; narrow it here rather than retyping the
 * array for its many other call sites.
 */
interface XGritPriceLineItem {
  title: string;
  regulatorySeatTime?: number;
  displayCouponList?: { discountAmount: number; discountType: string }[];
  product: {
    _id: string;
    courseId: string;
    type: string;
    filter: { app: string; type: string };
    pricing: { max: number };
    content?: { description?: string };
    schoolInformation?: string;
    sisterProductList?: { type: string; value: number; title: string; description: string }[];
  };
}

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  constructor(
    readonly httpClient: HttpClient,
    private xas: XGritApiService,
    private route: ActivatedRoute,
    private router: Router,
    private serverState: TransferState,
    private queryParamService: QueryParamService,
    private couponAggregationService: CouponAggregationService,
  ) {}

  private data = new BehaviorSubject(null);
  currentData = this.data.asObservable();

  /**
   * Products the page-data resolver pre-fetched for the current page (SSR, per request).
   * Set once by the resolver before any component renders, so components can read the
   * page's products synchronously in `ngOnInit` without depending on `setProduct()`
   * (which only `top-section`/`top-product` call, and only asynchronously). Used by
   * `rsa-product` to resolve the related Course `@id` for its Service schema. See EPS-3923.
   */
  private _pageProducts: XGritCompleteProduct[] = [];

  setPageProducts(products: XGritCompleteProduct[]): void {
    this._pageProducts = products ?? [];
  }

  get pageProducts(): XGritCompleteProduct[] {
    return this._pageProducts;
  }

  private setStateTransfer = (productId: CraftXGritProductEntry, type: string, state: XGritApiData) => {
    this.serverState.remove(makeStateKey<XGritApiData>(`${type}_${productId.xgritProductId}_${productId.xgritCoupon}`));
    this.serverState.set(
      makeStateKey<XGritApiData>(`${type}_${productId.xgritProductId}_${productId.xgritCoupon}`),
      state,
    );
  };

  private getStateTransfer = (productId: CraftXGritProductEntry, type: string) => {
    const falback = {
      checkoutLink: '',
      discountPrice: 0,
      originalPrice: 0,
      discountType: 'PERCENT',
      maxPrice: 0,
      type: '',
      prodcutFee: 0,
      filterType: '',
      title: '',
      app: '',
    };
    log({
      severity: SEVERITY.INFO,
      message: 'Getting State Transfer',
      contextId: productId.xgritProductId,
      type,
      couponId: productId.xgritCoupon,
      tags: ['ProductService.getStateTransfer'],
    });
    return this.serverState.get(
      makeStateKey<XGritApiData>(`${type}_${productId.xgritProductId}_${productId.xgritCoupon}`),
      falback,
    );
  };

  /**
   * queryProductEntry is used to get product API data from an XGritProductEntry.
   *
   * @param {CraftXGritProductEntry} productEntry - The product entry from Craft that you would like to get API data for
   * @return {Promise<PurchaseReceiptPriceResponse>} Returns a promise containing the product data returned by the xGrit API's 'purchase-receipt/price' endpoint
   *
   * @example
   *     queryProductEntry(productEntry)
   */
  private async queryProductEntry(productEntry: CraftXGritProductEntry): Promise<XGritPurchaseReceiptPriceResponse> {
    // COUPON OVERHAUL: Use CouponAggregationService to get ALL available coupons (banner, URL, email, etc.)
    const couponCollection = this.couponAggregationService?.collectAllCoupons([productEntry]);

    // Extract non-craft coupons (banner, URL, email capture)
    const nonCraftCoupons = this.extractNonCraftCoupons(couponCollection);

    // Combine this product's craft coupon with global coupons
    const individualCoupons = this.combineProductWithGlobalCoupons(productEntry, nonCraftCoupons);

    const couponCode = individualCoupons.length > 0 ? individualCoupons.join(',') : productEntry.xgritCoupon;

    const productPriceInfoResponse = this.xas.getProductPriceInfo(productEntry.xgritProductId, couponCode);
    const productApiData = lastValueFrom(productPriceInfoResponse);

    const liList = (await productApiData).lineItemList;
    if (liList == undefined || liList.length == 0) {
      return Promise.reject(new Error(`no product Id or course id returned for ${productEntry.xgritProductId}`));
    }

    return productApiData;
  }

  private async queryMultiProductEntries(
    multiProductEntries: CraftXGritProductEntry[],
  ): Promise<XgritPurchaseReceiptMultipriceResponse> {
    const priceSetList: ProductCodeLists[] = [];

    // Get ALL available coupons from aggregation (banner, URL, email, etc.)
    const couponCollection = this.couponAggregationService?.collectAllCoupons(multiProductEntries);

    // Extract non-craft coupons (banner, URL, email capture)
    const nonCraftCoupons = this.extractNonCraftCoupons(couponCollection);

    multiProductEntries.forEach((productEntry) => {
      // COUPON OVERHAUL: Create individual coupon set per product
      // Combine the product's craft coupon with global coupons (banner, URL, email)
      const individualCoupons = this.combineProductWithGlobalCoupons(productEntry, nonCraftCoupons);

      priceSetList.push({
        productIdList: [productEntry.xgritProductId],
        ...(individualCoupons.length > 0 && { couponCodeList: individualCoupons }),
      });
    });

    const multiProductPriceInfoResponse = this.xas.getMultiProductPriceInfo(priceSetList);
    const multiProductApiData = lastValueFrom(multiProductPriceInfoResponse);

    const liList = (await multiProductApiData).priceSetList;
    if (liList == undefined || liList.length == 0) {
      const productIds = multiProductEntries.map((entry) => entry.xgritProductId).join(',');
      return Promise.reject(new Error(`no products Id or courses id returned for ${productIds}`));
    }

    return multiProductApiData;
  }

  /**
   * Extracts non-craft coupons from coupon collection (banner, URL, email/phone capture, builder).
   * Delegates to `getAllCouponCodes` so every source is split and deduped the same way the
   * checkout URL and multiprice calls see it; the craft coupons are dropped here because
   * `combineProductWithGlobalCoupons` adds each product's own craft coupon per price set.
   */
  private extractNonCraftCoupons(couponCollection?: CouponCollectionRequest): string[] {
    if (!couponCollection) return [];

    return this.couponAggregationService.getAllCouponCodes({
      ...couponCollection,
      coupons: { ...couponCollection.coupons, craft: undefined },
    });
  }

  /**
   * Combines individual product craft coupon with global coupons
   */
  private combineProductWithGlobalCoupons(productEntry: CraftXGritProductEntry, globalCoupons: string[]): string[] {
    const combinedCoupons: string[] = [];

    // Add the product's individual craft coupon(s)
    if (productEntry.xgritCoupon) {
      const craftCoupons = productEntry.xgritCoupon
        .split(',')
        .map((coupon) => coupon.trim())
        .filter((coupon) => Boolean(coupon));
      combinedCoupons.push(...craftCoupons);
    }

    // Add global coupons (banner, URL, email)
    combinedCoupons.push(...globalCoupons);

    return [...new Set(combinedCoupons)]; // Remove duplicates
  }

  /** getProductFee is used to determine if an incoming product from xgrit has an accompying fee. This is done  by exploring the lineItemList[0].productList array to find if it contains an object with the field 'type' === 'FEE', and returning the pricing.current field (a number) for that object. It will return 0 if there is no match.
   *
   * @param {XGritPurchaseReceiptPriceResponse} productApiData - Product API data
   * @return {number} - Returns the fee for the product
   *
   * @example
   *    getProductFee(productApiData)
   */
  private getFeeData(productApiData: XGritPurchaseReceiptPriceResponse): { fee: number; description: string } {
    const feeData = { fee: 0, description: '' };
    let feeCount = 0;
    const lineItem = productApiData.lineItemList[0] as XGritPriceLineItem;
    if (!lineItem.product.sisterProductList) {
      return feeData;
    }
    lineItem.product.sisterProductList.forEach((product) => {
      if (product.type === 'FEE') {
        feeData.fee += product.value;
        let feeDescriptionTemplate = `<strong>${product.title}</strong> <p>${product.description}</p>`;

        if (feeCount > 0) {
          feeDescriptionTemplate = '<br>' + feeDescriptionTemplate;
        }
        feeCount++;
        feeData.description += feeDescriptionTemplate;
      }
    });
    return feeData;
  }

  /**
   * constructProductData is used to turn product API data of type PurchaseReceiptPriceResponse and a product entry of type XGritProductEntry into a product data instance of type XGritProductData.
   *
   * @param {PurchaseReceiptPriceResponse} productApiData - Product API data
   * @param {CraftXGritProductEntry} productEntry - Product entry from Craft
   * @return {XGritApiData} - Returns API data for the product
   *
   * @example
   *     constructProductData(productApiData, productEntry)
   */
  private constructProductData(
    productApiData: XGritPurchaseReceiptPriceResponse,
    productEntry: CraftXGritProductEntry,
  ): XGritApiData {
    const lineItem = productApiData.lineItemList[0] as XGritPriceLineItem;
    const xgritProductId = lineItem.product._id;
    const xgritCourseId = lineItem.product.courseId;
    const xgritBundleCoupon = productEntry.xgritBundleCoupon;
    const xgritDisableRsa = productEntry.xgritProductDisableRsa;
    const productState = productEntry.productState ? productEntry.productState : undefined;
    const appType = lineItem.product.filter.app;
    const productFees = this.getFeeData(productApiData).fee;
    const feeDescription = this.getFeeData(productApiData).description;

    // COUPON OVERHAUL: Use applied coupons from API response for checkout URL
    const xgritCheckoutUrl = this.xas.getCheckoutUrlWithAppliedCoupons(
      xgritProductId,
      xgritCourseId,
      productApiData,
      appType,
      xgritDisableRsa,
      productState,
      xgritBundleCoupon,
    );

    const rawDescription: string | undefined = lineItem.product?.content?.description;
    const description = rawDescription ? rawDescription.replace(/\n+/g, ' ').trim() : undefined;

    const apiData: XGritApiData = {
      originalPrice: productApiData.grossTotal,
      discountPrice: productApiData.total,
      discountType: this.searchForDiscountType(productApiData),
      maxPrice: lineItem.product.pricing.max,
      checkoutLink: xgritCheckoutUrl,
      productFees: productFees,
      feeDescription: feeDescription,
      type: lineItem.product.type,
      filterType: lineItem.product.filter.type,
      title: lineItem.title,
      app: lineItem.product.filter.app,
      disableRsa: xgritDisableRsa,
      description,
      regulatorySeatTime: lineItem.regulatorySeatTime || undefined,
      schoolInformation: lineItem.product?.schoolInformation || undefined,
    };

    return apiData;
  }

  searchForDiscountType(
    productApiData: Pick<XGritPurchaseReceiptPriceResponse, 'lineItemList' | 'grossDiscount'>,
  ): string {
    const lineItem = productApiData.lineItemList[0] as XGritPriceLineItem;
    const appliedCoupon = lineItem.displayCouponList?.find((coupon) => {
      return coupon.discountAmount === productApiData.grossDiscount;
    });
    return appliedCoupon ? appliedCoupon.discountType : 'PERCENT';
  }

  //sets product to be read by sibling components
  setProduct(product: XGritCompleteProduct): void {
    this.data.next(product);
    this.data.next(new Object());
  }

  //testing to ensure that product entry is valid
  isProductValid(productEntry: CraftXGritProductEntry | CraftXGritProductEntry[]): boolean {
    if (Array.isArray(productEntry)) {
      return productEntry.length > 0 && productEntry[0].xgritProductId !== null;
    }
    return productEntry !== undefined && productEntry !== null && productEntry.xgritProductId !== null;
  }

  //returning product whether or not it is in an array
  extractProduct(productEntry: CraftXGritProductEntry | CraftXGritProductEntry[]): CraftXGritProductEntry {
    if (Array.isArray(productEntry)) {
      return productEntry[0];
    }
    return productEntry;
  }

  /**
   * getProductData returns a "complete" xGrit product that combines the pricing data from the API and the CMS data into a single object (as a Promise).
   *
   * @param {CraftXGritProductEntry} productEntry - Product entry from Craft
   * @return {Promise<XGritCompleteProduct>} - Returns an object containing the original craftData from the CMS as well as data retrieved from the API
   *
   * @example
   *     constructProductData(productEntry)
   */
  async getProductData(productEntry: CraftXGritProductEntry): Promise<XGritCompleteProduct> {
    let data: XGritApiData;
    const productEntryToReturn = {
      craftData: productEntry,
      apiData: data,
    };
    let productApiError: unknown;
    const productApiData = await this.queryProductEntry(productEntry).catch((err: unknown) => {
      productApiError = err;
    });

    // If there was no API request error, pull out the xgrit API, else use State Transfer.
    if (!productApiError) {
      if (productApiData) {
        data = this.constructProductData(productApiData, productEntry);
        productEntryToReturn.apiData = data;

        try {
          this.setStateTransfer(productEntry, 'productEntryApiData', data);
        } catch (error) {
          logError({
            error: error as LogError,
            contextId: productEntry?.xgritProductId,
            tags: ['State Transfer', 'ProductService.getProductData'],
          });
        }
      }
    } else {
      productEntryToReturn.apiData = this.getStateTransfer(productEntry, 'productEntryApiData');
    }

    return productEntryToReturn;
  }

  /**
   * Gets the current page path from the router for TransferState key generation
   * Mimics PageDataResolver's fullPathFromArray logic
   */
  private getCurrentPagePath(): string {
    const urlSegments = this.router.url.split('/').filter((segment) => segment && !segment.includes('?'));
    return urlSegments.length === 0 ? 'home' : urlSegments.join('/');
  }

  /**
   * Checks if product data was pre-fetched by PageDataResolver during SSR
   * Returns cached data if available, otherwise returns null
   */
  private getPageLevelProductData(): XGritCompleteProduct[] | null {
    const fullPath = this.getCurrentPagePath();
    const PRODUCT_DATA_KEY = makeStateKey<XGritCompleteProduct[]>('product-data-' + fullPath);

    if (this.serverState.hasKey(PRODUCT_DATA_KEY)) {
      const cachedData = this.serverState.get<XGritCompleteProduct[]>(PRODUCT_DATA_KEY, null);
      // Remove the key so subsequent navigations fetch fresh data
      this.serverState.remove(PRODUCT_DATA_KEY);
      return cachedData;
    }

    return null;
  }

  async getMultiProductData(productEntries: CraftXGritProductEntry[]): Promise<XGritCompleteProduct[]> {
    // OPTIMIZATION: Check if product data was pre-fetched during SSR by PageDataResolver
    // This prevents duplicate API calls and ensures pricing is in the initial HTML
    const pageLevelCachedData = this.getPageLevelProductData();

    if (pageLevelCachedData && pageLevelCachedData.length > 0) {
      // Match cached products with requested product entries
      const matchedProducts: XGritCompleteProduct[] = [];

      productEntries.forEach((entry) => {
        const cachedProduct = pageLevelCachedData.find(
          (product) =>
            product.craftData.xgritProductId === entry.xgritProductId &&
            (product.craftData.xgritCoupon === entry.xgritCoupon || !entry.xgritCoupon),
        );

        if (cachedProduct) {
          matchedProducts.push(cachedProduct);
        }
      });

      // If we found all requested products in cache, return them
      if (matchedProducts.length === productEntries.length) {
        return matchedProducts;
      }

      // Otherwise, fall through to API call (partial cache miss)
    }

    // ORIGINAL LOGIC: Fetch from API if no cache or cache miss
    let multiProductApiError: unknown;
    const multiProductDataToReturn: XGritCompleteProduct[] = [];
    const multiProductApiData = await this.queryMultiProductEntries(productEntries).catch((err: unknown) => {
      multiProductApiError = err;
    });

    if (!multiProductApiError && multiProductApiData) {
      multiProductApiData.priceSetList.forEach((productApiData, index) => {
        if (productApiData.lineItemList) {
          const data = this.constructProductData(productApiData, productEntries[index]);
          const productEntryToReturn = {
            craftData: productEntries[index],
            apiData: data,
          };
          multiProductDataToReturn.push(productEntryToReturn);

          try {
            this.setStateTransfer(productEntries[index], 'productEntryApiData', data);
          } catch (error) {
            logError({
              error: error as LogError,
              contextId: productEntries[index]?.xgritProductId,
              tags: ['State Transfer', 'ProductService.getMultiProductData'],
            });
          }
        }
      });
    } else {
      productEntries.forEach((productEntry) => {
        const productEntryToReturn = {
          craftData: productEntry,
          apiData: this.getStateTransfer(productEntry, 'productEntryApiData'),
        };
        multiProductDataToReturn.push(productEntryToReturn);
      });
    }
    return multiProductDataToReturn;
  }
}
